import {useEffect, useState, useRef} from "react"
import { motion } from "framer-motion";
import { EtHeroMoment2 } from "./EtHeroMoment2";

// 28 Oct 2025 - Growth trail facets
const growthTrailFacets = [
  "Limitless...",
  "Revenue Growth",
  "Client Trust & Influence",
  "Market Trust & Influence",
  "Team Trust & Inspiration",
  "Self Awareness and Purpose",
];

// 28 Oct 2025 - Trail Variants
const trailVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.6,
    },
  },
};

// 28 Oct 2025 - Item Variants
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

// 28 Oct 2025 - Growth myth list
const growthMythList = [
  "Wishing and pushing",
  "Hustling and hassling",
  "Being paranoid and ruthless",
  "Winning at any cost",
];

// 28 Oct 2025 - Variant for fly-in
const mythTrailVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.4,
    },
  },
};

const mythItemVariants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};



export default function EtHero({onContinue}) {

  // First Created - 15 Aug 2025
  // Author - Atul Pandey
  // Updated - 27 Oct 2025 - Trinity Moment Refinements - Moment 1 - Intro - Hello! 
  // Updated - 27 Oct 2025 - Trinity Moment Refinements - Moment 2 - Growth Prompt - What does growth...
  // Updated - 28 Oct 2025 - Trinity Moment Refinements - Moment 3 - Acknowledgement - I hear you..
  // Updated - 29 Oct 2025 - Trinity Moment Refinements - onContinue prop
  // Updated - 30 Oct 2025 - to control display of "continue" and "stop" buttons post continue
  // Updated - 30 Oct 2025 - to control mobile display - continue on scroll

  // 1 - States for Trinity Moments
  const [collapsed, setCollapsed] = useState(false);    // false = full intro, true = chip
  const [chipExpanded, setChipExpanded] = useState(false); // click to toggle full text inside chip
  const advanceLockRef = useRef(false); // prevents double-advance
  const [moment, setMoment] = useState(1); // moment tracker

  const [growthInput, setGrowthInput] = useState('');
  const [savedGrowth, setSavedGrowth] = useState('');

  const [acknowledged, setAcknowledged] = useState(false); // 28 Oct 2025 - Moment 3

  const [hasContinued, setHasContinued] = useState(false); // 30 Oct 2025 - to control display of "continue" and "stop" buttons post continue


  // 2-  Advance to Moment 2 (and collapse to chip)
  const advance = () => {
    if (advanceLockRef.current) return;
    advanceLockRef.current = true;
    setCollapsed(true);
    // step to Moment 2 inside same section
    setTimeout(() => {
      setMoment(2);
      advanceLockRef.current = false;
    }, 400);
  };

  // 3a - Keyboard + wheel support (low-key, user-paced)
  useEffect(() => {
    const onKey = (e) => {
      if (["ArrowDown", "PageDown", " ", "Enter"].includes(e.key)) {
        e.preventDefault();
        advance();
      }
      if (["ArrowUp", "PageUp"].includes(e.key) && collapsed) {
        setChipExpanded(true);
      }
    };

    const onWheel = (e) => {
      // ignore scroll events from inside inputs or textareas
      const tag = e.target?.tagName?.toLowerCase();
      const isTypingField = tag === "input" || tag === "textarea";
      if (!isTypingField && e.deltaY > 30) {
        advance();
      }
    };

    const guardedKeyHandler = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      const isTypingField = tag === "input" || tag === "textarea";
      if (!isTypingField) onKey(e);
    };

    window.addEventListener("keydown", guardedKeyHandler);
    window.addEventListener("wheel", onWheel, { passive: true });

    return () => {
      window.removeEventListener("keydown", guardedKeyHandler);
      window.removeEventListener("wheel", onWheel);
    };
  }, [collapsed]);

  // 3b - Mobile Scroll - 30 Oct 2025
  useEffect(() => {
    let startY = 0;

    const onTouchStart = (e) => {
      startY = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      const currentY = e.touches[0].clientY;
      if (startY - currentY > 40) {
        // user swiped up
        advance();
      }
    };

    window.addEventListener("touchstart", onTouchStart);
    window.addEventListener("touchmove", onTouchMove);

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [collapsed]);


  // 4 - Save to local storage
  useEffect(() => {
    const cached = localStorage.getItem('trinity.hero.growthDefinition');
    if (cached) setSavedGrowth(cached);
  }, []);

  // 5 - Handle Submit Growth - local storage for now
  const handleSubmitGrowth = () => {
    const val = growthInput.trim();
    if (!val) return;
    localStorage.setItem('trinity.hero.growthDefinition', val);
    setSavedGrowth(val);
    setAcknowledged(true); // ⬅️ 28 Oct 2025 - triggers Moment 3
  };

  // 6 

  return (
    <>
      <section
        id="home"
        className={`flex justify-center relative bg-gradient-to-br from-gray-900 via-black to-orange-900 text-white   transition-[min-height] duration-500 font-poppins
          ${collapsed ? "min-h-[56vh] md:min-h-[48vh] items-start pt-20 md:pt-24" : "h-screen items-center"}
        `}
      >
        {/* Animated Background Overlay */}
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

        {/* Collapsed header chip (appears after advance) */}
        {collapsed && (
          <div 
            className="w-full flex flex-col items-center px-4"
          >
            <div className="sticky top-4 z-20">
              <button
                onClick={() => setChipExpanded((v) => !v)}
                className="max-w-[90vw] md:max-w-xl text-left rounded bg-white/5 border border-white/10 backdrop-blur px-4 py-2 hover:bg-white/10 transition"
                aria-expanded={chipExpanded}
              >
              <div className="flex items-start gap-2">
                <span className="mt-[6px] inline-block h-2 w-2 rounded-full bg-orange-500/70" />
                <div className="text-sm md:text-base text-white/90 leading-snug">
                  {!chipExpanded ? (
                    <span>
                      <strong className="text-orange-600">Trinity:</strong> Hello. I’m Trinity. I help tech-led leaders
                      design and compound growth.
                    </span>
                  ) : (
                    <span className="block">
                      <strong>Trinity</strong>
                      <br />Hello.
                      <br />I’m Trinity.
                      <br />I help tech-led leaders design and compound growth.
                    </span>
                  )}
                </div>
              </div>
            </button>
            </div>

            {/* Trinity Moment 2 – What is Growth */}
            {moment === 2 && (
              <motion.div
                className="relative z-20 my-5 py-5 w-full max-w-2xl mx-auto px-4 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* <p className="text-xl md:text-2xl text-gray-200 mb-6">
                  What does <span className="text-orange-400 font-semibold">growth</span> mean to you?
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <input
                    type="text"
                    value={growthInput}
                    onChange={(e) => setGrowthInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmitGrowth()}
                    placeholder={savedGrowth ? `Previously: ${savedGrowth}` : 'Your view in one line...'}
                    className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                  <button
                    onClick={handleSubmitGrowth}
                    className="px-6 py-3 rounded-lg bg-orange-600 hover:bg-orange-700 font-semibold"
                  >
                    Share
                  </button>
                </div> */}

                <p className="text-xl md:text-2xl text-gray-200 mb-6">
                  What does <span className="text-orange-400 font-semibold">growth</span> mean to you?
                </p>

                {!acknowledged && (
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <input
                      type="text"
                      value={growthInput}
                      onChange={(e) => setGrowthInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSubmitGrowth()}
                      placeholder={savedGrowth ? `Previously: ${savedGrowth}` : 'Your view in one line...'}
                      className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    <button
                      onClick={handleSubmitGrowth}
                      className="px-6 py-3 rounded-lg bg-orange-600 hover:bg-orange-700 font-semibold"
                    >
                      Share
                    </button>
                  </div>
                )}
                {acknowledged && moment === 2 && savedGrowth && (    
                  <p className="text-sm text-white/80">
                    <strong className="text-orange-600">You :</strong> “ <span className="font-extralight">{savedGrowth}</span> ”
                  </p> 
                )}
              </motion.div>
            )}

            {/* Trinity Moment 3 - I hear you... */}
            {acknowledged && moment === 2 && (
              <motion.div
                className="relative z-20 mb-10 mt-2 w-full max-w-xl mx-auto px-4 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <div className="flex justify-center">
                  <div className="max-w-[90vw] md:max-w-xl text-left rounded bg-white/5 border border-white/10 backdrop-blur px-4 py-3 transition">
                    <div className="flex items-start gap-2">
                      <span className="mt-[6px] inline-block h-2 w-2 rounded-full bg-orange-500/70" />
                      <div className="text-sm md:text-base text-white/90 leading-snug">
                        <strong className="text-orange-600">Trinity:</strong> I hear you. That’s a powerful view of growth.
                      </div>
                    </div>
                  </div>
                </div>

              </motion.div>
            )}

            {/* Trinity Moment 4 - Reframe growth as a trail */}
            {acknowledged && moment === 2 && (
              <motion.div
                className="relative z-20 mt-2 mb-10 w-full max-w-xl mx-auto px-4 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <div className="flex justify-center">
                  <div className="max-w-[90vw] md:max-w-xl text-left rounded bg-white/5 border border-white/10 backdrop-blur px-4 py-3 transition">
                    <div className="flex items-start gap-2">
                      <span className="mt-[6px] inline-block h-2 w-2 rounded-full bg-orange-500/70" />
                      <div className="text-sm md:text-base text-white/90 leading-snug">
                        <strong className="text-orange-600">Trinity:</strong> However, growth is not a one-time destination, but a never-ending trail.A trail of...
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Trinity Moment 5 - Trail of Growth Facets */}
            {acknowledged && moment === 2 && (
              <motion.div
                className="relative z-20 mb-5 w-full max-w-xl mx-auto px-4 text-center"
                variants={trailVariants}
                initial="hidden"
                animate="show"
              >
                
                {growthTrailFacets.map((facet, i) => (
                  <motion.p
                    key={facet}
                    variants={itemVariants}
                    className="text-white/90 text-base md:text-lg mb-2 font-light"
                  >
                    {facet}
                  </motion.p>
                ))}
              </motion.div>
            )}

             {acknowledged && moment === 2 && (<div className="w-full max-w-xs mx-auto my-8 h-px bg-orange-500/40 animate-pulse" />)}

            {/* Trinity Moment 6 – The Crossroads Challenge */}
            {acknowledged && moment === 2 && (
              <motion.div
                className="relative z-20 w-full max-w-xl mx-auto px-4 text-center font-light"
                variants={trailVariants}
                initial="hidden"
                animate="show"
              >
                <motion.p variants={itemVariants} className="text-white/90 text-base md:text-lg mb-2">
                  You may say…
                </motion.p>
                <motion.p variants={itemVariants} className="text-white/90 text-base md:text-lg mb-2">
                  You know this already.
                </motion.p>
                <motion.p variants={itemVariants} className="text-white/90 text-base md:text-lg mb-2">
                  If you feel that you have it all figured out…
                </motion.p>
                <motion.p variants={itemVariants} className="text-white/90 text-base md:text-lg mb-2">
                  We can <span className="text-orange-600 font-bold">stop</span> here.
                </motion.p>
                <motion.p variants={itemVariants} className="text-white/90 text-base md:text-lg mb-2">
                  {/* However, if you feel you may want to explore further… */}
                  However, if you're ready to <span className="text-orange-400 font-semibold">redesign your growth thinking</span>
                </motion.p>
                <motion.p variants={itemVariants} className="text-white/90 text-base md:text-lg mb-2">
                  We must <span className="text-orange-600 font-bold animate-pulse">continue</span>!
                </motion.p>
              </motion.div>
            )}

            {acknowledged && moment === 2 && (<div className="w-full max-w-xs mx-auto my-8 h-px bg-orange-500/40 animate-pulse" />)}

            {/* Trinity Moment 7 - Growth is NOT... */}
            {acknowledged && moment === 2 && (
              <>
                <motion.div
                  className="relative z-20 mb-10 w-full max-w-xl mx-auto px-4 text-center font-light"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.2 }}
                >
                  <p className="text-lg md:text-xl text-white/90 ">
                    For creating <span className="text-orange-400 font-semibold">limitless growth</span> takes a lot more than...
                  </p>
                </motion.div>

                <motion.div
                  className="relative z-20 mb-10 w-full max-w-xl mx-auto px-4 text-center font-light"
                  variants={mythTrailVariants}
                  initial="hidden"
                  animate="show"
                >
                  {growthMythList.map((myth, i) => (
                    <motion.p
                      key={myth}
                      variants={mythItemVariants}
                      className="text-white/90 text-base md:text-lg mb-2"
                    >
                      {myth}
                    </motion.p>
                  ))}
                </motion.div>
              </>
            )}

            {/* Trinity Moment 8 - Turning Point Invite */}
            {!hasContinued && acknowledged && moment === 2 && (
              <motion.div
                className="relative z-20 mb-10 mt-4 w-full max-w-xl mx-auto px-4 text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.6 }}
              >
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => {
                      onContinue?.();
                      // Scroll to advantage section after state unlocks
                      setTimeout(() => {
                        document.querySelector("#advantage")?.scrollIntoView({ behavior: "smooth" });
                      }, 100); 
                      setHasContinued(true);
                    }}
                    className="flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg shadow-lg font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Let's continue
                  </button>

                  <button
                    onClick={() => {
                      window.scrollTo(0, 0);
                      setTimeout(() => window.location.reload(), 10);

                      // window.location.reload()
                    }}
                    className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 px-6 py-3 rounded-lg shadow-inner font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Let's stop
                  </button>
                </div>
              </motion.div>
            )}

          </div>
        )}

        {/* Subtle radial glow behind the text (slow pulse) */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="w-[60vw] h-[60vw] max-w-[800px] max-h-[800px] rounded-full bg-orange-500/10 blur-3xl animate-[pulse_10s_ease-in-out_infinite]" />
        </div>

        {/* Content: three calm lines + Continue */}
        {!collapsed && (
          <motion.div
            className="text-center z-10 px-4 max-w-3xl"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            role="region"
            aria-label="Trinity introduction"
          >
            <motion.p
              className="text-3xl md:text-5xl font-semibold mb-2"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Hello.
            </motion.p>

            <motion.p
              className="text-2xl md:text-4xl font-semibold mb-3"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.6 }}
            >
              I’m Trinity.
            </motion.p>

            <motion.p
              className="text-lg md:text-2xl text-gray-200"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7, duration: 0.6 }}
            >
              I help tech-led leaders design and compound growth.
            </motion.p>

            {/* Primary action (no auto-advance) */}
            <motion.div
              className="mt-8"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.4, duration: 0.6 }}
            >
              <button
                onClick={advance}
                className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg shadow-lg font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                Continue
              </button>
              <p className="mt-2 text-sm text-gray-400">Scroll or press ↓ to continue</p>
            </motion.div>
          </motion.div>
        )}

        {/* {collapsed && <div className="h-12 md:h-16" />} */}
        {collapsed && <div className="min-h-screen" />}

      </section>
    </>
  );
}